import InfoCsvExportButton from "./InfoCsvExportButton.js";
import { useState } from "react";
import { Box, Typography, Button, IconButton, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "jspdf-autotable";

const SpreadsheetEditor = ({ title, data, onSave, onBack, onDelete }) => {
    const [spreadsheetData, setSpreadsheetData] = useState(
        data || [[""]]
    );

    const handleCellChange = (rowIndex, colIndex, value) => {
        const newData = [...spreadsheetData];
        newData[rowIndex][colIndex] = value;
        setSpreadsheetData(newData);
    };

    const addRow = () => {
        const colCount = spreadsheetData[0]?.length || 1;
        setSpreadsheetData([...spreadsheetData, Array(colCount).fill("")]);
    };

    const addColumn = () => {
        setSpreadsheetData(spreadsheetData.map(row => [...row, ""]));
    };

    const deleteRow = (rowIndex) => {
        if (spreadsheetData.length > 1) {
            setSpreadsheetData(spreadsheetData.filter((_, idx) => idx !== rowIndex));
        }
    };

    const deleteColumn = (colIndex) => {
        if (spreadsheetData[0]?.length > 1) {
            setSpreadsheetData(
                spreadsheetData.map(row => row.filter((_, idx) => idx !== colIndex))
            );
        }
    };

    const handleSave = () => {
        onSave(spreadsheetData);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();

        doc.addFont('https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.setFont('Roboto');

        doc.setFontSize(16);
        doc.text(title, 14, 20);

        autoTable(doc, {
            startY: 30,
            head: [],
            body: spreadsheetData,
            theme: 'grid',
            styles: {
                font: 'Roboto',
                fontSize: 10,
                cellPadding: 3,
            },
            headStyles: {
                fillColor: [245, 245, 245],
                textColor: [0, 0, 0],
                font: 'Roboto',
            },
        });

        doc.save(`${title}.pdf`);
    };

    return (
        <Box sx={{ padding: "2rem", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "2rem", gap: 2 }}>
                <IconButton onClick={onBack} color="primary">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5">{title}</Typography>
                <Box sx={{ marginLeft: "auto", display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                    >
                        Αποθήκευση
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleExportPDF}
                    >
                        Εξαγωγή PDF
                    </Button>
                    <InfoCsvExportButton filename={title} getRows={() => spreadsheetData}
                        disabled={!spreadsheetData.some(
                            (row) => row.some((value) => value != null && value !== ""),
                        )} />
                    {onDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteForeverIcon />}
                            onClick={onDelete}
                        >
                            Διαγραφή
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, marginBottom: "1rem" }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    size="small"
                >
                    Προσθήκη Γραμμής
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addColumn}
                    size="small"
                >
                    Προσθήκη Στήλης
                </Button>
            </Box>

            <Box sx={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                        <tr>
                            <th style={{ width: "40px", border: "1px solid #ddd", padding: "4px" }}></th>
                            {spreadsheetData[0]?.map((_, colIndex) => (
                                <th
                                    key={colIndex}
                                    style={{
                                        border: "1px solid #ddd",
                                        padding: "4px",
                                        backgroundColor: "#f5f5f5",
                                        position: "relative",
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => deleteColumn(colIndex)}
                                        disabled={spreadsheetData[0].length === 1}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {spreadsheetData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td
                                    style={{
                                        border: "1px solid #ddd",
                                        padding: "4px",
                                        backgroundColor: "#f5f5f5",
                                        textAlign: "center",
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => deleteRow(rowIndex)}
                                        disabled={spreadsheetData.length === 1}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </td>
                                {row.map((cell, colIndex) => (
                                    <td
                                        key={colIndex}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: "0",
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            multiline
                                            value={cell}
                                            onChange={(e) =>
                                                handleCellChange(rowIndex, colIndex, e.target.value)
                                            }
                                            variant="standard"
                                            InputProps={{
                                                disableUnderline: true,
                                                style: { padding: "8px" },
                                            }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    );
};

export default SpreadsheetEditor;
